import { useEffect, useRef } from 'react';

/**
 * Hook that runs a callback on every animation frame.
 *
 * Based on: https://css-tricks.com/using-requestanimationframe-with-react-hooks/
 *
 * @param callback - Called each frame with delta time (ms since last frame).
 * @param enabled - When false, the animation loop is paused.
 */
export const useAnimationFrame = (
  callback: (deltaTime: number) => void,
  enabled: boolean = true,
): void => {
  const callbackRef = useRef(callback);
  const frameRef = useRef<number>();
  const previousTimeRef = useRef<number>();

  // Keep callback ref fresh without re-running effect
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const animate = (time: number): void => {
      if (previousTimeRef.current !== undefined) {
        const deltaTime = time - previousTimeRef.current;
        callbackRef.current(deltaTime);
      }
      previousTimeRef.current = time;
      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      previousTimeRef.current = undefined;
    };
  }, [enabled]);
};
